/**
 * PostHog product analytics.
 *
 * Browser-only. `initAnalytics()` is called once from src/index.js before the
 * app mounts; every other export is a safe no-op until that happens, so the
 * build-time prerender (src/entry-server.jsx runs this tree under Node) and any
 * environment without a project token simply record nothing.
 *
 * Two things about this app shape the config below:
 *
 *  1. It is a single-page app. Pageviews are captured from `history` changes
 *     rather than by a React Router effect, which keeps posthog-js out of the
 *     render path and means marketing routes are counted even before hydration
 *     finishes.
 *  2. The authenticated half handles veterinary clinical-trial records and
 *     customer contact details. Autocapture (which records the text of the
 *     element you clicked) is therefore limited to the public marketing pages,
 *     session recording is off, and dashboard URLs are rewritten to their route
 *     pattern before any event leaves the browser — see `scrubEvent`.
 */
import posthog from "posthog-js";

const projectToken = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
const apiHost = import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";

export const isAnalyticsConfigured = Boolean(projectToken);

let started = false;

/**
 * Path segments that identify a single record rather than a screen. Patients,
 * orders and trials are addressed by UUID; a handful of older records use a
 * numeric (timestamp-derived) id.
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_ID_RE = /^\d{4,}$/;

/**
 * Collapse record ids in a dashboard path down to `:id`.
 *
 * `/dashboard/patients/6f1c…/treatment` becomes `/dashboard/patients/:id/treatment`.
 * This both keeps record identifiers out of a third party's event stream and
 * makes the numbers usable — without it every patient page is its own URL and
 * the "most viewed screens" report is one row per patient.
 *
 * Marketing paths are left alone: `/about/team/:memberId` is a public slug with
 * real SEO/analytics value, and `/products/bio-ply` must stay verbatim.
 */
function normalizePathname(pathname) {
  if (!pathname.startsWith("/dashboard")) return pathname;
  return pathname
    .split("/")
    .map((segment) =>
      UUID_RE.test(segment) || NUMERIC_ID_RE.test(segment) ? ":id" : segment
    )
    .join("/");
}

/**
 * Query parameters that are credentials or record ids rather than campaign
 * data. The approve/decline smart link carries its signed token as `t` (and the
 * enrollee as `u`), the enrollment invite as `invite` (and `lead`), and Supabase
 * auth links use `code` / `token` / `token_hash`. None of them belong in a third
 * party's event stream: the invite token alone unlocks that lead's prefilled
 * contact details for 14 days.
 */
const SENSITIVE_PARAMS = [
  "t",
  "u",
  "invite",
  "lead",
  "code",
  "token",
  "token_hash",
  "access_token",
  "refresh_token",
];

function normalizeUrl(url) {
  try {
    const parsed = new URL(url);
    parsed.pathname = normalizePathname(parsed.pathname);
    SENSITIVE_PARAMS.forEach((name) => parsed.searchParams.delete(name));
    // A key=value fragment is an auth payload, not an in-page anchor. A password
    // recovery / set-password link lands as `#access_token=…&refresh_token=…`,
    // and PostHog captures the first pageview before the Supabase client has
    // stripped it — a live session for that account. Plain `#section` anchors
    // are kept.
    if (parsed.hash.includes("=")) parsed.hash = "";
    return parsed.toString();
  } catch {
    // Not an absolute URL — leave it untouched rather than dropping the value.
    return url;
  }
}

/**
 * Whether a URL is an emailed enrollment invite (/enroll?lead=…&invite=…).
 * The token itself is scrubbed above; this keeps the one fact analytics needs
 * from it — that the visit came from an invite — so invite-link signups can be
 * counted as a `from_invite` event property.
 */
function isInviteUrl(url) {
  try {
    return new URL(url).searchParams.has("invite");
  } catch {
    return false;
  }
}

/**
 * Normalize every same-origin URL in a property bag, whatever key it sits
 * under. posthog-js copies the landing URL into several properties
 * (`$current_url`, `$session_entry_url`, `$initial_current_url`, …) and the set
 * has grown between releases, so matching on the value is what keeps a new one
 * from slipping through. External URLs (a real referrer) are left as-is.
 */
function scrubUrls(bag) {
  if (!bag || typeof bag !== "object") return;
  const { origin } = window.location;
  Object.keys(bag).forEach((key) => {
    const value = bag[key];
    if (typeof value === "string" && value.startsWith(origin)) {
      bag[key] = normalizeUrl(value);
    }
  });
}

/**
 * Last stop before an event is sent. Runs for every event, including the ones
 * posthog-js generates itself (pageviews, pageleaves, autocapture), which is
 * why the path rewriting lives here rather than at each call site.
 */
export function scrubEvent(event) {
  const props = event?.properties;
  if (!props) return event;

  // Read before the scrub below deletes the invite parameters.
  if (
    typeof props.$current_url === "string" &&
    isInviteUrl(props.$current_url)
  ) {
    props.from_invite = true;
  }

  // $current_url, plus $referrer on an in-app navigation (the previous
  // dashboard URL, which carries the same ids), plus the person-property copies
  // posthog-js sends alongside them.
  scrubUrls(props);
  scrubUrls(props.$set);
  scrubUrls(props.$set_once);

  if (typeof props.$pathname === "string") {
    props.$pathname = normalizePathname(props.$pathname);
  }

  return event;
}

const LOCAL_HOSTS = ["localhost", "127.0.0.1", "[::1]", "0.0.0.0"];

/**
 * Whether this page load should be recorded at all.
 *
 * Local development is excluded. Building a feature means reloading the same
 * pages dozens of times, and every one of those was landing in the same project
 * the Analytics screen reports on — inflating pageviews, adding `localhost:5173`
 * to the referrer list, and pushing throwaway events into "Tracked events".
 * There is one project, so the only place to draw the line is here.
 *
 * Deployed environments (including Vercel previews) still record: their traffic
 * is real people on a real URL. Set VITE_POSTHOG_ALLOW_DEV=true in .env.local
 * when you specifically need to verify tracking from a dev server.
 */
function shouldRecord() {
  if (import.meta.env.VITE_POSTHOG_ALLOW_DEV === "true") return true;
  if (import.meta.env.DEV) return false;
  const host = window.location.hostname;
  // A production build served locally (`npm run preview`) isn't DEV, so check
  // the hostname too.
  return !LOCAL_HOSTS.includes(host) && !host.endsWith(".local");
}

/**
 * Initialize the client. Idempotent, and a no-op outside the browser, in local
 * development, or when VITE_POSTHOG_PROJECT_TOKEN is unset (local checkouts,
 * CI, the prerender).
 */
export function initAnalytics() {
  if (started || !isAnalyticsConfigured || typeof window === "undefined") {
    return;
  }
  if (!shouldRecord()) return;
  started = true;

  posthog.init(projectToken, {
    api_host: apiHost,
    // Opts into PostHog's current recommended defaults rather than the legacy
    // ones kept for backwards compatibility. The explicit options below win.
    defaults: "2025-05-24",
    // Captures the initial load plus every pushState/popstate, so React Router
    // navigations are counted without a tracking component in the tree.
    capture_pageview: "history_change",
    capture_pageleave: true,
    // Only create a person profile once someone signs in (see identifyUser).
    // Anonymous marketing traffic is still counted, it just doesn't mint a
    // person record per visitor.
    person_profiles: "identified_only",
    // Autocapture records the text/attributes of clicked elements. Restricted
    // to the public site: inside /dashboard those elements are patient names,
    // client details and order contents. Deliberate product events belong in
    // captureEvent() calls instead.
    autocapture: { url_ignorelist: [/\/dashboard/] },
    // Session replay of the dashboard would record clinical records verbatim.
    // Turning it on is a decision to make with a signed BAA-equivalent and
    // masking rules in place, not a default.
    disable_session_recording: true,
    before_send: scrubEvent,
    // An invite visitor's signup event can fire after the page has navigated
    // away from the invite URL, so carry the flag for the rest of the session
    // (lead_submitted, the first dashboard visit, …) rather than only on
    // events whose URL still has it.
    loaded: (client) => {
      if (isInviteUrl(window.location.href)) {
        client.register_for_session({ from_invite: true });
      }
    },
  });
}

/** The underlying client, for <PostHogProvider client={…}> in src/index.js. */
export { posthog };

/**
 * Tie subsequent events to a signed-in user. Safe to call repeatedly: PostHog
 * only merges the anonymous history into the person on the first call for a
 * given distinct id, and later calls just refresh the person properties.
 *
 * The distinct id is the Supabase user id (stable across email changes); the
 * email and access flags ride along as person properties so funnels can be cut
 * by admin / shop / trials audience.
 */
export function identifyUser({
  id,
  email,
  isAdmin,
  shopAccess,
  trialsAccess,
} = {}) {
  if (!started || !id) return;
  posthog.identify(id, {
    email,
    is_admin: Boolean(isAdmin),
    shop_access: Boolean(shopAccess),
    trials_access: Boolean(trialsAccess),
  });
}

/**
 * Clear the identity on sign-out so the next person at a shared clinic
 * workstation starts a new anonymous id instead of inheriting the last one.
 *
 * Only call this on an actual sign-out — `reset()` also throws away the
 * anonymous id and campaign attribution of an ordinary visitor.
 */
export function resetUser() {
  if (!started) return;
  posthog.reset();
}

/**
 * Record a product event, e.g. captureEvent("order_placed", { total }).
 * Never pass patient or client identifiers in `properties`.
 */
export function captureEvent(name, properties) {
  if (!started) return;
  posthog.capture(name, properties);
}
